import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from 'prop-types';

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { alpha } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import { FormControl, InputLabel, Select, MenuItem, Button } from "@mui/material";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAvatar from "components/MDAvatar";
import MDSnackbar from "components/MDSnackbar";

// RasPm Dashboard examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import dataTableData from "./data/dataTableData";
import { RequestService } from "services/RequestService";

// Form Components
import EmployeeForm from "../add";
import EmployeeUpdateForm from "../update";

// Validation
import validateEmployee from "../schemas/validations";
import MDInput from "components/MDInput";

function EmployeesDataTable() {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState(dataTableData);
  const [loading, setLoading] = useState(true);
  const [showDeleteEmployees, setShowDeleteEmployees] = useState(false);
  const [notification, setNotification] = useState({
    color: "info",
    icon: "notifications",
    title: "لوحة ادارة ربان السفينة",
    content: "",
    dateTime: new Date().toLocaleString('ar-IQ'),
    open: false,
  });
  const [updateDialog, setUpdateDialog] = useState({
    open: false,
    employeeData: null
  });
  const [addDialog, setAddDialog] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [managers, setManagers] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [natureOfWorkOptions, setNatureOfWorkOptions] = useState([]);
  const [managersOptions, setManagersOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [isResignedFilter, setIsResignedFilter] = useState("");
  const [isArchivedFilter, setIsArchivedFilter] = useState("");
  const [search, setSearch] = useState("");
  const [firstSearch, setFirstSearch] = useState(false);
  const [employeeForm, setEmployeeForm] = useState({
    name: "",
    email: "",
    phone: "",
    departmentId: "",
    positionId: "",
    managerId: "",
    dob: "",
    joinDate: "",
    gender: true,
    natureOfWork: null,
    fingerPrintId: null,
    contractType: "",
    degree: "",
    major: "",
    jobTitle: "",
    badgeNumber: "",
    profileImageUrl: "",
    isResigned: false,
    salary: ""
  });

  const closeNotification = () => setNotification({ ...notification, open: false });

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalItems: 0
  });

  const showNotification = (message, type = "success", backendResponse = null) => {
    setNotification({
      color: type,
      icon: type === "success" ? "check" : "error",
      title: "لوحة ادارة ربان السفينة",
      content: backendResponse?.data?.message || message,
      dateTime: new Date().toLocaleString('ar-IQ'),
      open: true
    });
  };

  const handleUpdateClick = (employeeData) => {
    const formatDate = (dateString) => {
      if (!dateString) return '';
      return new Date(dateString).toISOString().split('T')[0];
    };

    setEmployeeForm({
      name: employeeData.name || '',
      email: employeeData.email || '',
      phone: employeeData.phone || '',
      departmentId: employeeData.departmentId || '',
      positionId: employeeData.positionId || '',
      managerId: employeeData.managerId || '',
      dob: formatDate(employeeData.dob),
      joinDate: formatDate(employeeData.joinDate),
      gender: employeeData.gender,
      natureOfWork: employeeData.natureOfWork || null,
      fingerPrintId: employeeData.fingerPrintId || '',
      contractType: employeeData.contractType || '',
      degree: employeeData.degree || '',
      major: employeeData.major || '',
      jobTitle: employeeData.jobTitle || '',
      badgeNumber: employeeData.badgeNumber || '',
      profileImageUrl: employeeData.profileImageUrl || '',
      isResigned: employeeData.isResigned
    });
    setUpdateDialog({ open: true, employeeData });
  };

  const handleCloseUpdateDialog = () => {
    setUpdateDialog({ open: false, employeeData: null });
    setEmployeeForm({
      name: "",
      email: "",
      phone: "",
      departmentId: "",
      positionId: "",
      managerId: "",
      dob: "",
      joinDate: "",
      gender: true,
      natureOfWork: null,
      fingerPrintId: null,
      contractType: "",
      degree: "",
      major: "",
      jobTitle: "",
      badgeNumber: "",
      profileImageUrl: "",
      isResigned: false,
      salary: ""
    });
  };

  const handleUpdateSubmit = async () => {
    try {
      const validationErrors = validateEmployee(employeeForm);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);

        const missingFields = Object.entries(validationErrors).map(([field, error]) => {
          const fieldNames = {
            name: "اسم الموظف / Employee Name",
            email: "البريد الإلكتروني / Email",
            phone: "رقم الهاتف / Phone Number",
            departmentId: "القسم / Department",
            positionId: "المنصب / Position",
            dob: "تاريخ الميلاد / Date of Birth",
            joinDate: "تاريخ الإلتحاق / Join Date",
            natureOfWork: "طبيعة العمل / Nature of Work",
            contractType: "نوع العقد / Contract Type",
            degree: "الدرجة العلمية / Degree",
            jobTitle: "المسمى الوظيفي / Job Title",
            badgeNumber: "رقم الشارة / Badge Number",
            fingerPrintId: "رقم البصمة / Fingerprint ID",
            gender: "الجنس / Gender",
            managerId: "المدير / Manager",
            major: "التخصص / Major",
            profileImageUrl: "صورة الملف الشخصي / Profile Image",
            salary: "الراتب / Salary"
          };
          return fieldNames[field] || field;
        }).join(", ");

        showNotification(`يرجى ملء الحقول التالية: ${missingFields}`, "error");
        return;
      }

      setLoading(true);
      const formData = new FormData();

      const apiMapping = {
        phone: 'phone',
        dob: 'dob',
        joinDate: 'joinDate',
        fingerPrintId: 'fingerPrintId'
      };

      Object.keys(employeeForm).forEach(key => {
        const apiKey = apiMapping[key] || key;
        if (key === 'dob' || key === 'joinDate') {
          const date = employeeForm[key] ? new Date(employeeForm[key]).toISOString() : null;
          formData.append(apiKey, date);
        } else if (key === 'fingerPrintId' || key === 'natureOfWork') {
          formData.append(apiKey, Number(employeeForm[key]) || 0);
        } else if (employeeForm[key] !== null && employeeForm[key] !== undefined) {
          formData.append(apiKey, employeeForm[key]);
        }
      });

      const response = await RequestService.putFormData(`Employees/${updateDialog.employeeData.id}`, employeeForm);


      if (response.data && !response.data.error) {
        showNotification("تم تحديث بيانات الموظف بنجاح", "success", response);
        handleCloseUpdateDialog();
        fetchEmployees();
      } else {
        showNotification("حدث خطأ أثناء تحديث بيانات الموظف", "error", response);
      }
    } catch (error) {
      console.error("Error updating employee:", error);
      showNotification(error.response?.data?.message || "حدث خطأ أثناء تحديث بيانات الموظف", "error", error.response);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setEmployeeForm({
      name: "",
      email: "",
      phone: "",
      departmentId: "",
      positionId: "",
      managerId: "",
      dob: "",
      joinDate: "",
      gender: true,
      natureOfWork: null,
      fingerPrintId: null,
      contractType: "",
      degree: "",
      major: "",
      jobTitle: "",
      badgeNumber: "",
      profileImageUrl: "",
      isResigned: false,
      salary: ""
    });
    setAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialog(false);
    setEmployeeForm({
      name: "",
      email: "",
      phone: "",
      departmentId: "",
      positionId: "",
      managerId: "",
      dob: "",
      joinDate: "",
      gender: true,
      natureOfWork: null,
      fingerPrintId: null,
      contractType: "",
      degree: "",
      major: "",
      jobTitle: "",
      badgeNumber: "",
      profileImageUrl: "",
      isResigned: false,
      salary: ""
    });
    setErrors({});
  };

  const handleAddSubmit = async () => {
    try {
      const validationErrors = validateEmployee(employeeForm);

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);

        const missingFields = Object.entries(validationErrors).map(([field, error]) => {
          const fieldNames = {
            name: "اسم الموظف / Employee Name",
            email: "البريد الإلكتروني / Email",
            phone: "رقم الهاتف / Phone Number",
            departmentId: "القسم / Department",
            positionId: "المنصب / Position",
            dob: "تاريخ الميلاد / Date of Birth",
            joinDate: "تاريخ الإلتحاق / Join Date",
            natureOfWork: "طبيعة العمل / Nature of Work",
            managerId: "المدير / Manager",
            contractType: "نوع العقد / Contract Type",
            degree: "الدرجة العلمية / Degree",
            jobTitle: "المسمى الوظيفي / Job Title",
            badgeNumber: "رقم الشارة / Badge Number",
            fingerPrintId: "رقم البصمة / Fingerprint ID",
            gender: "الجنس / Gender",
            managerId: "المدير / Manager",
            major: "التخصص / Major",
            profileImageUrl: "صورة الملف الشخصي / Profile Image",
            salary: "الراتب / Salary"
          };
          return fieldNames[field] || field;
        }).join(", ");

        showNotification(`يرجى ملء الحقول التالية: ${missingFields}`, "error");
        return;
      }

      setLoading(true);
      const formData = new FormData();

      const apiMapping = {
        phone: 'phone',
        dob: 'dob',
        joinDate: 'joinDate',
        fingerPrintId: 'fingerPrintId'
      };

      Object.keys(employeeForm).forEach(key => {
        const apiKey = apiMapping[key] || key;
        if (key === 'dob' || key === 'joinDate') {
          const date = employeeForm[key] ? new Date(employeeForm[key]).toISOString() : null;
          formData.append(apiKey, date);
        } else if (key === 'fingerPrintId' || key === 'natureOfWork') {
          formData.append(apiKey, Number(employeeForm[key]) || 0);
        } else if (employeeForm[key] !== null && employeeForm[key] !== undefined) {
          formData.append(apiKey, employeeForm[key]);
        }
      });

      const response = await RequestService.postFormData("Employees", employeeForm, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && !response.data.error) {
        showNotification("تم إضافة الموظف بنجاح", "success", response);
        handleCloseAddDialog();
        fetchEmployees();
      } else {
        showNotification("حدث خطأ أثناء إضافة الموظف", "error", response);
      }
    } catch (error) {
      console.error("Error adding employee:", error);
      if (error.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        const formattedErrors = {};

        Object.keys(backendErrors).forEach(key => {
          const fieldName = key.charAt(0).toLowerCase() + key.slice(1);
          formattedErrors[fieldName] = backendErrors[key][0];
        });

        setErrors(formattedErrors);
        showNotification("يرجى تصحيح الأخطاء في النموذج", "error", error.response);
      } else {
        showNotification(error.response?.data?.message || "حدث خطأ أثناء إضافة الموظف", "error", error.response);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEmployeeForm(prev => {
      const newForm = {
        ...prev,
        [field]: field === 'natureOfWork' ? Number(value) : value
      };
      return newForm;
    });
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleBlur = (field) => {
    const validationErrors = validateEmployee(employeeForm);
    if (validationErrors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: validationErrors[field]
      }));
    }
  };

  const handleShowDeleteEmployeesClick = () => {
    const newShowDelete = !showDeleteEmployees;
    setShowDeleteEmployees(newShowDelete);
    if (newShowDelete) {
      setIsResignedFilter("");
    }
  };

  const handleArchiveEmployee = async (id) => {
    try {
      setLoading(true);
      const response = await RequestService.delete(`Employees/${id}`);

      if (response.data && !response.data.error) {
        showNotification("تم أرشفة الموظف بنجاح", "success", response);
        fetchEmployees();
      } else {
        showNotification("حدث خطأ أثناء أرشفة الموظف", "error", response);
      }
    } catch (error) {
      console.error("Error archiving employee:", error);
      showNotification(error.response?.data?.message || "حدث خطأ أثناء أرشفة الموظف", "error", error.response);
    } finally {
      setLoading(false);
    }
  };

  const transformEmployeeData = (employees) => {
    return employees
      .filter(employee => {
        if (showDeleteEmployees) {
          return employee.isArchived && !employee.isResigned;
        } else {
          return !employee.isArchived &&
            (isResignedFilter === "" ||
              employee.isResigned === (isResignedFilter === "true"));
        }
      })
      .map((employee, index) => ({
        index: index + 1,
        id: employee.id,
        status: showDeleteEmployees ? "محذوف" : employee.isResigned ? "مستقيل" : "مستمر",
        name: employee.name || "—",
        departmentName: employee.departmentName || "—",
        positionName: employee.positionName || "—",
        email: employee.email || "—",
        phone: employee.phone || "—",
        actions: (
          <MDBox display="flex" alignItems="center" gap={1}>
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              onClick={() => handleShowDetails(employee)}
              startIcon={<Icon>visibility</Icon>}
            >
              عرض
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              onClick={() => handleUpdateClick(employee)}
              startIcon={<Icon>edit</Icon>}
            >
              تعديل
            </MDButton>
            {!employee.isResigned && employee.isArchived ? "" :
              <MDButton
                variant="outlined"
                color="error"
                size="small"
                onClick={() => handleArchiveEmployee(employee.id)}
                startIcon={<Icon>delete</Icon>}
              >
                حذف
              </MDButton>
            }
          </MDBox>
        ),
      }));
  };

  const fetchDepartments = async () => {
    try {
      const response = await RequestService.get("Departments/get-departments");
      if (response.data && !response.data.error) {
        setDepartments(response.data.data);
      } else {
        showNotification("حدث خطأ أثناء جلب الأقسام", "error", response);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
      showNotification(error.response?.data?.message || "حدث خطأ أثناء جلب الأقسام", "error", error.response);
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await RequestService.get("Positions/get-positions");
      if (response.data && !response.data.error) {
        setPositions(response.data.data);
      } else {
        showNotification("حدث خطأ أثناء جلب المناصب", "error", response);
      }
    } catch (error) {
      console.error("Error fetching positions:", error);
      showNotification(error.response?.data?.message || "حدث خطأ أثناء جلب المناصب", "error", error.response);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await RequestService.get("Employees/GetManagers");
      if (response.data) {
        setManagers(response.data);
      } else {
        showNotification("حدث خطأ أثناء جلب قائمة المدراء", "error", response);
      }
    } catch (error) {
      console.error("Error fetching managers:", error);
      showNotification(error.response?.data?.message || "حدث خطأ أثناء جلب قائمة المدراء", "error", error.response);
    }
  };

  const fetchDegrees = async () => {
    try {
      const response = await RequestService.get("Employees/degrees");
      if (response.data) {
        setDegrees(response.data);
      } else {
        showNotification("حدث خطأ أثناء جلب الدرجات العلمية", "error", response);
      }
    } catch (error) {
      console.error("Error fetching degrees:", error);
      showNotification(error.response?.data?.message || "حدث خطأ أثناء جلب الدرجات العلمية", "error", error.response);
    }
  };

  const fetchContractTypes = async () => {
    try {
      const response = await RequestService.get("Employees/contract-type");
      if (response.data) {
        setContractTypes(response.data);
      } else {
        showNotification("حدث خطأ أثناء جلب أنواع العقود", "error", response);
      }
    } catch (error) {
      console.error("Error fetching contract types:", error);
      showNotification(error.response?.data?.message || "حدث خطأ أثناء جلب أنواع العقود", "error", error.response);
    }
  };

  const fetchNatureOfWorkOptions = async () => {
    try {
      const response = await RequestService.get("Employees/nature-of-work");
      if (!response.error && response.data.data) {
        setNatureOfWorkOptions(response.data.data);
      } else {
        showNotification("حدث خطأ أثناء جلب طبيعة العمل", "error", response);
      }
    } catch (error) {
      console.error("Error fetching nature of work options:", error);
      showNotification(error.response?.data?.message || "حدث خطأ أثناء جلب طبيعة العمل", "error", error.response);
    }
  };

  const fetchManagersOptions = async () => {
    try {
      const response = await RequestService.get("Employees/managers");
      if (!response.error && response.data.data) {
        setManagersOptions(response.data.data);
        setManagers(response.data.data)
      } else {
        showNotification("حدث خطأ أثناء جلب المدراء", "error", response);
      }
    } catch (error) {
      console.error("Error fetching nature of work options:", error);
      showNotification(error.response?.data?.message || "حدث خطأ أثناء جلب المدراء", "error", error.response);
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const skip = (pagination.page - 1) * pagination.pageSize;
      const take = pagination.pageSize;

      const response = await RequestService.get(`/Employees?isResigned=${showDeleteEmployees ? false : isResignedFilter}&isArchived=${showDeleteEmployees}&skip=${skip}&take=${take}&name=${search}`);
      if (response.data && !response.data.error) {
        setTableData({
          ...tableData,
          rows: transformEmployeeData(response.data.data)
        });
        setPagination({
          ...pagination,
          totalItems: response.data.totalCount
        });
      } else {
        showNotification("حدث خطأ أثناء جلب الموظفين", "error", response);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      setTimeout(() => {
        showNotification(error.message || "حدث خطأ أثناء جلب الموظفين", "error", error.response);
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const handlePageSizeChange = (newSize) => {
    setPagination({ page: 1, pageSize: newSize, totalItems: pagination.totalItems });
  };


  useEffect(() => {
    fetchEmployees();
  }, [isResignedFilter, isArchivedFilter, showDeleteEmployees, pagination.page, pagination.pageSize]);

  useEffect(() => {
    if (search || firstSearch) {
      setFirstSearch(true); {
        setPagination(prev => ({ ...prev, page: 1 }));
        const timer = setTimeout(() => {
          fetchEmployees();
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [search]);

  const handleShowDetails = (employee) => {
    setSelectedEmployee(employee);
    setDetailsDialog(true);
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialog(false);
    setSelectedEmployee(null);
  };

  const renderDetailsDialog = () => (
    <Dialog
      open={detailsDialog}
      onClose={handleCloseDetailsDialog}
      maxWidth="xl"
      fullWidth
      dir="rtl"
      PaperProps={{
        sx: {
          width: '90%',
          maxWidth: '1400px',
          m: 2,
          borderRadius: 1,
          height: 'auto',
          maxHeight: '90vh',
          backgroundColor: (theme) => theme.palette.background.default
        }
      }}
    >
      <DialogTitle>
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDBox display="flex" alignItems="center">
            <Icon sx={{ mr: 1, color: 'info.main' }}>person</Icon>
            <MDTypography variant="h6" fontWeight="medium">
              تفاصيل الموظف / Employee Details
            </MDTypography>
          </MDBox>
          <IconButton
            onClick={handleCloseDetailsDialog}
            sx={{
              color: 'error.main',
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
              },
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent sx={{ p: 3, overflowY: 'auto' }}>
        {selectedEmployee && (
          <MDBox>
            {/* Basic Info Card */}
            <Card sx={{ mb: 3 }}>
              <MDBox p={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <MDBox>
                      <MDTypography variant="h4" fontWeight="medium">
                        {selectedEmployee.name}
                      </MDTypography>
                      <MDTypography variant="body2" color="text">
                        {positions.find(p => p.id === selectedEmployee.positionId)?.name || '-'}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>

            {/* Information Sections */}
            <Grid container spacing={3}>
              {/* Personal Information Card */}
              <Grid item xs={12} md={6}>
                <Card>
                  <MDBox p={2} display="flex" alignItems="center">
                    <Icon sx={{ mr: 1, color: 'info.main' }}>person</Icon>
                    <MDTypography variant="h6" fontWeight="medium">
                      المعلومات الشخصية / Personal Information
                    </MDTypography>
                  </MDBox>
                  <MDBox px={2} pb={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <MDBox display="flex" alignItems="center" mb={2}>
                          <Icon sx={{ mr: 1, color: 'info.main' }}>email</Icon>
                          <MDBox>
                            <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                              البريد الإلكتروني / Email
                            </MDTypography>
                            <MDTypography variant="body2">{selectedEmployee.email}</MDTypography>
                          </MDBox>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MDBox display="flex" alignItems="center" mb={2}>
                          <Icon sx={{ mr: 1, color: 'info.main' }}>phone</Icon>
                          <MDBox>
                            <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                              رقم الهاتف / Phone
                            </MDTypography>
                            <MDTypography variant="body2">{selectedEmployee.phone || '-'}</MDTypography>
                          </MDBox>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MDBox display="flex" alignItems="center">
                          <Icon sx={{ mr: 1, color: 'info.main' }}>cake</Icon>
                          <MDBox>
                            <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                              تاريخ الميلاد / Date of Birth
                            </MDTypography>
                            <MDTypography variant="body2">
                              {selectedEmployee.dob ? new Date(selectedEmployee.dob).toLocaleDateString() : '-'}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>

              {/* Work Information Card */}
              <Grid item xs={12} md={6}>
                <Card>
                  <MDBox p={2} display="flex" alignItems="center">
                    <Icon sx={{ mr: 1, color: 'info.main' }}>work</Icon>
                    <MDTypography variant="h6" fontWeight="medium">
                      معلومات العمل / Work Information
                    </MDTypography>
                  </MDBox>
                  <MDBox px={2} pb={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <MDBox display="flex" alignItems="center" mb={2}>
                          <Icon sx={{ mr: 1, color: 'info.main' }}>business</Icon>
                          <MDBox>
                            <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                              القسم / Department
                            </MDTypography>
                            <MDTypography variant="body2">
                              {departments.find(d => d.id === selectedEmployee.departmentId)?.name || '-'}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MDBox display="flex" alignItems="center" mb={2}>
                          <Icon sx={{ mr: 1, color: 'info.main' }}>business</Icon>
                          <MDBox>
                            <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                              المدير / Manager
                            </MDTypography>
                            <MDTypography variant="body2">
                              {managers.find(d => d.id === selectedEmployee.managerId)?.name || '-'}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MDBox display="flex" alignItems="center" mb={2}>
                          <Icon sx={{ mr: 1, color: 'info.main' }}>schedule</Icon>
                          <MDBox>
                            <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                              طبيعة العمل / Nature of Work
                            </MDTypography>
                            <MDTypography variant="body2">
                              {selectedEmployee.natureOfWork || '-'}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MDBox display="flex" alignItems="center">
                          <Icon sx={{ mr: 1, color: 'info.main' }}>date_range</Icon>
                          <MDBox>
                            <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                              تاريخ الالتحاق / Join Date
                            </MDTypography>
                            <MDTypography variant="body2">
                              {selectedEmployee.joinDate ? new Date(selectedEmployee.joinDate).toLocaleDateString() : '-'}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>

              {/* Education Information Card */}
              <Grid item xs={12} md={6}>
                <Card>
                  <MDBox p={2} display="flex" alignItems="center">
                    <Icon sx={{ mr: 1, color: 'info.main' }}>school</Icon>
                    <MDTypography variant="h6" fontWeight="medium">
                      المعلومات التعليمية / Education Information
                    </MDTypography>
                  </MDBox>
                  <MDBox px={2} pb={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <MDBox display="flex" alignItems="center" mb={2}>
                          <Icon sx={{ mr: 1, color: 'info.main' }}>school</Icon>
                          <MDBox>
                            <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                              المؤهل العلمي / Degree
                            </MDTypography>
                            <MDTypography variant="body2">
                              {degrees.find(d => d.id === selectedEmployee.degree)?.name || '-'}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MDBox display="flex" alignItems="center">
                          <Icon sx={{ mr: 1, color: 'info.main' }}>psychology</Icon>
                          <MDBox>
                            <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                              التخصص / Major
                            </MDTypography>
                            <MDTypography variant="body2">{selectedEmployee.major || '-'}</MDTypography>
                          </MDBox>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>

              {/* Additional Information Card */}
              <Grid item xs={12} md={6}>
                <Card>
                  <MDBox p={2} display="flex" alignItems="center">
                    <Icon sx={{ mr: 1, color: 'info.main' }}>more_horiz</Icon>
                    <MDTypography variant="h6" fontWeight="medium">
                      معلومات إضافية / Additional Information
                    </MDTypography>
                  </MDBox>
                  <MDBox px={2} pb={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <MDBox display="flex" alignItems="center" mb={2}>
                          <Icon sx={{ mr: 1, color: 'info.main' }}>badge</Icon>
                          <MDBox>
                            <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                              رقم البطاقة / Badge Number
                            </MDTypography>
                            <MDTypography variant="body2">{selectedEmployee.badgeNumber || '-'}</MDTypography>
                          </MDBox>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MDBox display="flex" alignItems="center" mb={2}>
                          <Icon sx={{ mr: 1, color: 'info.main' }}>fingerprint</Icon>
                          <MDBox>
                            <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                              رقم البصمة / Fingerprint ID
                            </MDTypography>
                            <MDTypography variant="body2">{selectedEmployee.fingerPrintId || '-'}</MDTypography>
                          </MDBox>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MDBox display="flex" alignItems="center">
                          <Icon sx={{ mr: 1, color: 'info.main' }}>description</Icon>
                          <MDBox>
                            <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                              نوع العقد / Contract Type
                            </MDTypography>
                            <MDTypography variant="body2">
                              {contractTypes.find(t => t.id === selectedEmployee.contractType)?.name || '-'}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </MDBox>
        )}
      </DialogContent>
    </Dialog>
  );

  const ActionCell = ({ row }) => (
    <MDBox display="flex" alignItems="center" gap={1}>
      <MDButton
        variant="outlined"
        color="info"
        size="small"
        onClick={() => handleShowDetails(row.original)}
        startIcon={<Icon>visibility</Icon>}
      >
        عرض
      </MDButton>
      <MDButton
        variant="gradient"
        color="info"
        size="small"
        onClick={() => handleUpdateClick(row.original)}
        startIcon={<Icon>edit</Icon>}
      >
        تعديل
      </MDButton>
      <MDButton
        variant="outlined"
        color="error"
        size="small"
        onClick={() => handleArchiveEmployee(row.original.id)}
        startIcon={<Icon>archive</Icon>}
      >
        أرشفة
      </MDButton>

    </MDBox>
  );

  ActionCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
        phone: PropTypes.string,
        departmentId: PropTypes.number,
        positionId: PropTypes.number,
        dob: PropTypes.string,
        joinDate: PropTypes.string,
        degree: PropTypes.number,
        major: PropTypes.string,
        jobTitle: PropTypes.string,
        badgeNumber: PropTypes.string,
        fingerPrintId: PropTypes.string,
        natureOfWork: PropTypes.number,
        managerId: PropTypes.number,
        contractType: PropTypes.number,
        gender: PropTypes.bool,
        isResigned: PropTypes.bool,
        profileImageUrl: PropTypes.string
      }).isRequired
    }).isRequired
  };

  const DepartmentCell = ({ value }) => (
    <MDTypography variant="body2">
      {departments.find(d => d.id === value)?.name || '-'}
    </MDTypography>
  );

  DepartmentCell.propTypes = {
    value: PropTypes.number
  };

  const PositionCell = ({ value }) => (
    <MDTypography variant="body2">
      {positions.find(p => p.id === value)?.name || '-'}
    </MDTypography>
  );

  PositionCell.propTypes = {
    value: PropTypes.number
  };

  const columns = [
    {
      Header: "الاسم / Name",
      accessor: "name",
      width: "20%"
    },
    {
      Header: "البريد الإلكتروني / Email",
      accessor: "email",
      width: "20%"
    },
    {
      Header: "القسم / Department",
      accessor: "departmentId",
      width: "15%",
      Cell: DepartmentCell
    },
    {
      Header: "المنصب / Position",
      accessor: "positionId",
      width: "15%",
      Cell: PositionCell
    },
    {
      Header: "الإجراءات / Actions",
      accessor: "actions",
      width: "30%",
      Cell: ActionCell
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchEmployees(),
          fetchDepartments(),
          fetchPositions(),
          // fetchManagers(),
          fetchDegrees(),
          fetchContractTypes(),
          fetchNatureOfWorkOptions(),
          fetchManagersOptions()
        ]);
      } catch (error) {
        console.error("Error fetching data:", error);
        showNotification("حدث خطأ أثناء جلب البيانات", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const renderDialog = (title, isOpen, onClose, onSubmit, isUpdate = false) => (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      dir="rtl"
      PaperProps={{
        sx: {
          width: '90%',
          maxWidth: '1400px',
          m: 2,
          borderRadius: 1,
          height: 'auto',
          maxHeight: '90vh',
          backgroundColor: (theme) => theme.palette.background.default
        }
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          px: 3,
          py: 2
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={{ p: 3, overflowY: 'auto' }}>
        <MDBox>
          {!isUpdate ? (
            <EmployeeForm
              formData={employeeForm}
              handleChange={handleInputChange}
              handleBlur={handleBlur}
              departments={departments}
              positions={positions}
              managers={managers}
              degrees={degrees}
              contractTypes={contractTypes}
              natureOfWorkOptions={natureOfWorkOptions}
              managersOptions={managersOptions}
              errors={errors}
            />
          ) : (
            <EmployeeUpdateForm
              formData={employeeForm}
              handleChange={handleInputChange}
              handleBlur={handleBlur}
              departments={departments}
              positions={positions}
              managers={managers}
              degrees={degrees}
              contractTypes={contractTypes}
              natureOfWorkOptions={natureOfWorkOptions}
              managersOptions={managersOptions}
              errors={errors}
            />
          )}
        </MDBox>
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          p: 2,
          px: 3,
          gap: 2
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}  >
          <MDButton onClick={onSubmit} variant="gradient" color="info">
            {isUpdate ? "تحديث" : "إضافة"}
          </MDButton>
          <MDButton onClick={onClose} variant="outlined" color="secondary">
            إلغاء
          </MDButton>
        </div>


      </DialogActions>
    </Dialog>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={3}
                sx={{
                  backgroundColor: "grey.100",
                  borderBottom: "1px solid",
                  borderColor: "grey.300",
                  flexWrap: "wrap",
                }}
              >
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium">
                    قائمة الموظفين
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    إدارة الموظفين وبياناتهم
                  </MDTypography>
                </MDBox>

                <MDBox display="flex" alignItems="center" gap={2} sx={{ flexWrap: "wrap" }}>
                  <MDBox sx={{ flex: 1, minWidth: "180px" }}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>حالة الاستقالة</InputLabel>
                      <Select
                        value={isResignedFilter}
                        onChange={(e) => setIsResignedFilter(e.target.value)}
                        label="حالة الاستقالة"
                        sx={{ height: "45px" }}
                        disabled={showDeleteEmployees}
                      >
                        <MenuItem value="">الكل</MenuItem>
                        <MenuItem value="true">مستقيل</MenuItem>
                        <MenuItem value="false">مستمر</MenuItem>
                      </Select>
                    </FormControl>
                  </MDBox>
                  <MDBox width="12rem" ml="auto">
                    <MDInput
                      placeholder="بحث ..."
                      value={search}
                      fullWidth
                      onChange={({ currentTarget }) => {
                        setSearch(currentTarget.value);
                      }}
                    />
                  </MDBox>

                  <MDButton
                    variant="gradient"
                    color="error"
                    startIcon={<Icon>delete</Icon>}
                    onClick={handleShowDeleteEmployeesClick}
                    sx={{ height: "45px", whiteSpace: "nowrap" }}
                  >
                    {showDeleteEmployees ? "العودة لقائمة الموظقين" : "عرض المحذوفين"}
                  </MDButton>

                  <MDButton
                    variant="gradient"
                    color="info"
                    startIcon={<Icon>add</Icon>}
                    onClick={handleAddClick}
                    sx={{ height: "45px", whiteSpace: "nowrap" }}
                  >
                    إضافة موظف
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox p={3}>
                <DataTable
                  showDeleteEmployees={showDeleteEmployees}
                  table={tableData}
                  entriesPerPage={{
                    defaultValue: pagination.pageSize,
                    entries: [5, 10, 15, 20, 25],
                    onChange: handlePageSizeChange
                  }}
                  isSorted={true}
                  showTotalEntries={true}
                  noEndBorder
                  loading={loading}
                  pagination={{
                    variant: "gradient",
                    color: "info",
                    page: pagination.page,
                    totalPages: Math.ceil(pagination.totalItems / pagination.pageSize),
                    totalItems: pagination.totalItems,
                    onPageChange: handlePageChange
                  }}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialogs */}
      {renderDialog("إضافة موظف جديد", addDialog, handleCloseAddDialog, handleAddSubmit, false)}
      {renderDialog("تحديث بيانات الموظف", updateDialog.open, handleCloseUpdateDialog, handleUpdateSubmit, true)}
      {renderDetailsDialog()}

      {/* Notification */}
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        dateTime={notification.dateTime}
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
        bgWhite
        dir="rtl"
      />

      <Footer />
    </DashboardLayout>
  );
}

export default EmployeesDataTable;